'use client';

import type { FC, PropsWithChildren } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { DynamicImage } from '@wwe-portal/ui/components/dynamic-image';
import { Each } from '@wwe-portal/ui/components/utils';

import { cn } from '@wwe-portal/ui/lib/lib';

import type { OfferItem, OfferRewardItem } from '../type';
import {
  parseOfferItemDescription,
  parseRosterOfferItemStats,
} from '@wwe-portal/utils/offer/helper';
import { ItemIconWithToolTip } from './item-icon-tooltip';
import { RosterItemStar } from './roster-item-star';

const Item: FC<
  PropsWithChildren<{
    text: string;
    id: string;
    isAggregate?: boolean;
    description: string;
  }>
> = ({ text, children, id, isAggregate, description }) => {
  return (
    <div className="flex flex-col items-center" data-id={id}>
      <div className="flex items-center justify-center">
        {isAggregate ? (
          children
        ) : (
          <ItemIconWithToolTip tooltipContent={description}>
            {children}
          </ItemIconWithToolTip>
        )}
      </div>
      <div className="flex h-6 max-w-[75px] items-center justify-center px-[2px] pt-2 text-base font-semibold text-[#E3E7F4]">
        <div>{text}</div>
      </div>
    </div>
  );
};

const MultipleItems: FC<{
  items: OfferItem[] | undefined;
  rewardItems?: OfferRewardItem[];
  isAggregate?: boolean;
  onItemClick?: (id: string) => void;
  storeItemDescription?: string;
}> = ({
  items,
  rewardItems,
  isAggregate,
  onItemClick,
  storeItemDescription,
}) => {
  const classNameScaled =
    'max-[376px]:w-[72px] max-[376px]:h-[91px] max-[360px]:w-[67px] max-[360px]:h-[85px] max-[320px]:w-[60px] max-[320px]:h-[76px]';

  return (
    <div className="px-6">
      {!isAggregate && storeItemDescription && (
        <div className="text-text500 font-poppins text-center text-xs font-normal leading-[18px]">
          {storeItemDescription}
        </div>
      )}
      <div className="no-scrollbar mb-5 mt-6 flex h-[296px] justify-center overflow-y-scroll overscroll-contain scroll-smooth px-7 max-[376px]:px-4">
        <div className="mb-8 grid w-full grid-cols-3 gap-5">
          {items && (
            <Each
              data={items}
              render={(item) => {
                const rosterItemStats = parseRosterOfferItemStats({
                  description: item.description,
                  text: item.text,
                  itemId: item.itemId,
                });
                return (
                  <Item
                    key={item.itemId}
                    description={parseOfferItemDescription({
                      description: item.description,
                      text: item.text,
                      stats: rosterItemStats,
                    })}
                    id={item.itemId}
                    isAggregate={isAggregate}
                    text={item.text ?? ''}
                  >
                    <ClickableDiv
                      className="relative"
                      onClick={() => onItemClick?.(item.itemId)}
                    >
                      {isAggregate && (
                        <div className="text-yellow800 text-2xs absolute -right-3 rounded-full bg-black p-1 text-center font-medium opacity-70">
                          {item.percentage ?? 0}%
                        </div>
                      )}
                      <DynamicImage
                        alt={item.itemId}
                        className={cn(
                          'h-[95px] w-[75px] object-contain',
                          classNameScaled
                        )}
                        defaultUrl={item.defaultImageUrl}
                        dynamicUrl={item.dynamicImageUrl}
                        resizedWidth={64}
                      />
                      <RosterItemStar stats={rosterItemStats} />
                      {item.label && (
                        <div className="absolute right-3 top-[50%] w-full -translate-y-1/2 -rotate-12 text-nowrap stroke-1 text-sm font-bold italic text-[#E3E7F4]">
                          {item.label}
                        </div>
                      )}
                    </ClickableDiv>
                  </Item>
                );
              }}
            />
          )}
          {rewardItems && (
            <Each
              data={rewardItems}
              render={(item) => (
                <Item
                  description={
                    item.description ?? item.quantity?.toString() ?? ''
                  }
                  id={item.itemId}
                  text={item.quantity?.toString() ?? ''}
                  key={item.itemId}
                >
                  <div className={cn('h-[95px] w-[75px]', classNameScaled)}>
                    {item.iconElement}
                  </div>
                </Item>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MultipleItems;
